import { useState } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  LinkIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { integrationsApi } from '../../api/client';

type VerificationMethod = 'oauth' | 'email' | 'pos';
type Aggregator = 'ubereats' | 'doordash' | 'grubhub';

interface VerifyOwnershipModalProps {
  storeId: string;
  storeName: string;
  aggregator: Aggregator;
  hasPOSConnected: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AGGREGATOR_LABELS: Record<Aggregator, string> = {
  ubereats: 'Uber Eats',
  doordash: 'DoorDash',
  grubhub: 'Grubhub',
};

export function VerifyOwnershipModal({
  storeId,
  storeName,
  aggregator,
  hasPOSConnected,
  isOpen,
  onClose,
  onSuccess,
}: VerifyOwnershipModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<VerificationMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Email verification form
  const [restaurantEmail, setRestaurantEmail] = useState('');

  if (!isOpen) return null;

  const aggregatorLabel = AGGREGATOR_LABELS[aggregator];

  async function handleOAuthVerification() {
    setLoading(true);
    setError('');

    try {
      const res = await integrationsApi.startOAuth(storeId, aggregator);
      // Redirect to OAuth URL
      window.location.href = res.data.redirect_url;
    } catch (err: unknown) {
      // OAuth not configured - suggest email verification
      setError('OAuth not configured for ' + aggregatorLabel + '. Please use Email Verification instead.');
      setSelectedMethod(null); // Return to method selection
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailVerification() {
    if (!restaurantEmail) {
      setError('Please enter your restaurant email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await integrationsApi.startEmailVerification(storeId, aggregator, restaurantEmail, storeName);
      setEmailSent(true);
    } catch (err: unknown) {
      // OAuth not configured - suggest email verification
      setError((err as any)?.response?.data?.message || 'Failed to send verification email.');
    } finally {
      setLoading(false);
    }
  }

  async function handlePOSVerification() {
    if (!hasPOSConnected) {
      setError('Please connect your POS system first.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // We need the POS type, but for simplicity we'll try with the first connected one
      const posRes = await integrationsApi.listPOS();
      const storePos = posRes.data.data.find(p => p.store_id === storeId && p.status === 'connected');

      if (!storePos) {
        setError('No connected POS found for this store.');
        setLoading(false);
        return;
      }

      await integrationsApi.startPOSVerification(storeId, aggregator, storePos.pos_type);
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: unknown) {
      // OAuth not configured - suggest email verification
      setError((err as any)?.response?.data?.message || 'POS verification failed.');
    } finally {
      setLoading(false);
    }
  }

  function handleMethodSelect(method: VerificationMethod) {
    setSelectedMethod(method);
    setError('');
    setEmailSent(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Verify Restaurant Ownership</h3>
            <p className="text-sm text-slate-500">{storeName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-10 h-10 text-emerald-600" />
              </div>
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Verified!</h4>
              <p className="text-sm text-slate-600">
                You can now submit disputes on behalf of {storeName}.
              </p>
            </div>
          ) : emailSent ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <EnvelopeIcon className="w-10 h-10 text-amber-600" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">Check Your Email</h4>
                <p className="text-sm text-slate-600">
                  We sent a verification link to:
                </p>
                <p className="text-sm font-medium text-slate-900 mt-1">{restaurantEmail}</p>
              </div>
              <p className="text-xs text-slate-500">
                Click the link in the email to complete verification. The link expires in 24 hours.
              </p>
              <button
                onClick={onClose}
                className="w-full py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200"
              >
                Done
              </button>
            </div>
          ) : !selectedMethod ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Required to submit disputes on your behalf to {aggregatorLabel}.
              </p>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Choose verification method:
              </p>

              <div className="space-y-3">
                {/* OAuth Option - disabled for Grubhub */}
                {aggregator === 'grubhub' ? (
                  <div className="w-full flex items-center gap-4 p-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-left opacity-60 cursor-not-allowed">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400">
                      <LinkIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-500">
                        Sign in with Grubhub
                      </div>
                      <div className="text-xs text-slate-400">
                        Requires business partnership - Contact Grubhub
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleMethodSelect('oauth')}
                    className="w-full flex items-center gap-4 p-4 bg-white border-2 border-slate-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-all text-left group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 group-hover:bg-amber-200">
                      <LinkIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 group-hover:text-amber-900">
                        Sign in with {aggregatorLabel}
                      </div>
                      <div className="text-xs text-slate-500">
                        Fastest - Instant verification
                      </div>
                    </div>
                  </button>
                )}

                {/* Email Option - DISABLED: Doesn't enable data access */}
                <div className="w-full flex items-center gap-4 p-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-left opacity-60 cursor-not-allowed">
                  <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400">
                    <EnvelopeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-500">
                      Email Verification
                    </div>
                    <div className="text-xs text-slate-400">
                      Not available - Use OAuth or upload CSV instead
                    </div>
                  </div>
                </div>

                {/* POS Option */}
                <button
                  onClick={() => handleMethodSelect('pos')}
                  disabled={!hasPOSConnected}
                  className="w-full flex items-center gap-4 p-4 bg-white border-2 border-slate-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:bg-white"
                >
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 group-hover:bg-orange-200 group-disabled:bg-slate-100 group-disabled:text-slate-400">
                    <ComputerDesktopIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 group-hover:text-amber-900 group-disabled:text-slate-500">
                      Verify via POS
                    </div>
                    <div className="text-xs text-slate-500">
                      {hasPOSConnected
                        ? 'Use connected POS to confirm'
                        : 'Connect your POS first'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          ) : selectedMethod === 'oauth' ? (
            <div className="space-y-4">
              <button
                onClick={() => setSelectedMethod(null)}
                className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                ← Back to options
              </button>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <LinkIcon className="w-8 h-8 text-amber-600" />
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  You'll be redirected to {aggregatorLabel} to verify your restaurant ownership.
                </p>
              </div>

              <button
                onClick={handleOAuthVerification}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/30 transition-all disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Redirecting...
                  </span>
                ) : (
                  `Continue with ${aggregatorLabel}`
                )}
              </button>
            </div>
          ) : selectedMethod === 'email' ? (
            <div className="space-y-4">
              <button
                onClick={() => setSelectedMethod(null)}
                className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                ← Back to options
              </button>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Restaurant Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={restaurantEmail}
                  onChange={(e) => setRestaurantEmail(e.target.value)}
                  placeholder="restaurant@example.com"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Enter the email registered with {aggregatorLabel}. We'll send a verification link.
                </p>
              </div>

              <button
                onClick={handleEmailVerification}
                disabled={loading || !restaurantEmail}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Send Verification Email'
                )}
              </button>
            </div>
          ) : selectedMethod === 'pos' ? (
            <div className="space-y-4">
              <button
                onClick={() => setSelectedMethod(null)}
                className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                ← Back to options
              </button>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <ComputerDesktopIcon className="w-8 h-8 text-orange-600" />
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  We'll verify your restaurant ownership using your connected POS system.
                </p>
              </div>

              <button
                onClick={handlePOSVerification}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-orange-700 hover:to-orange-600 shadow-lg shadow-amber-500/30 transition-all disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify via POS'
                )}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
